import { z } from 'zod'
import type { Locale } from './common.js'
import type { ModuleKey, Permission } from './permissions.js'
import type { ResidencyZone } from './platform.js'

export const loginRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  password: z.string().min(1).max(256),
})
export type LoginRequest = z.infer<typeof loginRequestSchema>

export interface SessionUser {
  id: string
  email: string
  name: string
  nameAr: string | null
  locale: Locale
  /** Set after an administrator assigns or resets the password; every other action is refused until it is changed. */
  mustChangePassword: boolean
  /**
   * The user's home org unit (ADR-025), or null. Always sent by the API; optional in the type only
   * until the web's session fixtures carry it.
   */
  homeOrgUnitId?: string | null
}

export interface SessionTenant {
  id: string
  slug: string
  name: string
  nameAr: string | null
  /** Where the company's data lives (01 §11), from `platform.tenants.residency_zone`. */
  residencyZone: ResidencyZone
}

/** Returned by POST /auth/login and GET /auth/me. */
export interface SessionInfo {
  user: SessionUser
  tenant: SessionTenant
  /** Effective permissions: what the user's roles grant, within the company's enabled modules. */
  permissions: Permission[]
  /**
   * What the user's roles grant, whatever the company's modules (D15). A switched-off module's
   * permissions are here but not in `permissions`: the role editor offers them (anti-escalation compares
   * role grants), and screens tell "turned off for your company" from "no permission". Always sent by
   * the API; optional in the type only until the web's session fixtures carry it (absent: `permissions`).
   */
  grantedPermissions?: Permission[]
  /**
   * Of `permissions`, those the user holds over only part of the records (a data scope other than ALL,
   * ADR-025). Readers that cover every record (aggregate reports, balances) refuse them with 403
   * SCOPE_RESTRICTED, so screens leave those figures out. Only permissions a scope narrows are listed
   * (`isScopeEnforced`): any other one reaches every record whatever its stored scope (an OWN scope on a
   * till role, say), and its readers serve every figure. Always sent by the API; optional in the type
   * only until the web's session fixtures carry it (absent: none are scoped).
   */
  scopedPermissions?: Permission[]
  /**
   * Modules enabled for the company (D15). Always sent by the API; optional in the type only until
   * the web's session fixtures carry it.
   */
  modules?: ModuleKey[]
  /**
   * Set when a super admin signed in as this user from the console (ADR-019): the app shows who, and
   * the session ends within the hour. Absent or null for the user's own sessions.
   */
  impersonatedBy?: { email: string; expiresAt: string } | null
}
